import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Music, Users, GraduationCap, School, Sparkles, Piano, ExternalLink, HandHeart, Share2, CreditCard, Receipt } from "lucide-react";
import PageHero from "../components/PageHero";
import { fetchCrowdfunding } from "../lib/api";
import { SCHOOL } from "../lib/site";

const USE_ICONS = [Music, Piano, GraduationCap, Heart, School, Sparkles];

export default function Sostienici() {
  const [cf, setCf] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    fetchCrowdfunding()
      .then((data) => {
        setCf(data);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, []);

  const donateUrl = cf?.campaign_url || null;

  return (
    <div data-testid="page-sostienici">
      <PageHero
        eyebrow="Crowdfunding · Erogazioni liberali"
        title="Sostieni"
        italic="la musica."
        subtitle="Una donazione liberale a favore di tutte le attività dell'associazione, didattiche e concertistiche. Ogni contributo, piccolo o grande, fa la differenza."
      />

      {!loaded ? (
        <div className="text-center py-24 text-[#5C5C5C]">Caricamento...</div>
      ) : (
        <>
          {/* Intro + donazione */}
          <section className="py-20 px-6 md:px-12 lg:px-24 max-w-[1400px] mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
              <div>
                <div className="text-[11px] tracking-eyebrow text-[#A3133A] mb-5">Perché donare</div>
                <h2 className="font-serif-display text-3xl md:text-4xl text-[#1C1C1C] leading-tight mb-8">
                  La musica è un bene di tutti. <em className="italic font-light">Aiutaci a farla crescere.</em>
                </h2>
                {(cf?.descrizione || []).map((p, i) => (
                  <p key={p} className="text-[#5C5C5C] leading-relaxed mb-5">{p}</p>
                ))}
                <div className="mt-8 border-l-2 border-[#A3133A] pl-5 text-sm text-[#5C5C5C] leading-relaxed" data-testid="nota-fiscale">
                  {cf?.nota_fiscale}
                </div>
              </div>

              {/* Donation box */}
              <div className="bg-[#FBFBF9] border border-[#A3133A]/15 p-8 md:p-10" data-testid="donation-box">
                {donateUrl ? (
                  <div className="text-center py-4">
                    <div className="text-[11px] tracking-eyebrow text-[#A3133A] mb-6">Dona ora — importo libero</div>
                    <HandHeart size={48} className="text-[#A3133A] mx-auto mb-6 opacity-70" />
                    <h3 className="font-serif-display text-2xl text-[#1C1C1C] mb-4">Ogni contributo conta.</h3>
                    <p className="text-[#5C5C5C] leading-relaxed mb-8 max-w-md mx-auto">
                      La donazione avviene in modo sicuro tramite SumUp: scegli tu liberamente l'importo
                      e paga con carta in pochi secondi.
                    </p>
                    <a
                      href={donateUrl}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="donate-button"
                      className="w-full bg-[#A3133A] text-white px-6 py-4 text-[12px] tracking-eyebrow rounded-sm inline-flex items-center justify-center gap-2 hover:bg-[#8B0F2F] transition-all"
                    >
                      <Heart size={14} /> Dona ora con SumUp
                    </a>
                    <div className="mt-6 flex flex-col gap-3 text-left">
                      <div className="flex items-start gap-3 text-sm text-[#5C5C5C]">
                        <CreditCard size={16} className="text-[#A3133A] mt-0.5 shrink-0" />
                        <span>Pagamento sicuro con carta di credito o debito tramite SumUp.</span>
                      </div>
                      <div className="flex items-start gap-3 text-sm text-[#5C5C5C]">
                        <Receipt size={16} className="text-[#A3133A] mt-0.5 shrink-0" />
                        <span>Ricevuta della donazione disponibile su richiesta: scrivi a {SCHOOL.email}.</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-6" data-testid="campaign-coming-soon">
                    <HandHeart size={48} className="text-[#A3133A] mx-auto mb-6 opacity-70" />
                    <h3 className="font-serif-display text-2xl text-[#1C1C1C] mb-4">La campagna sta per partire.</h3>
                    <p className="text-[#5C5C5C] leading-relaxed mb-8 max-w-md mx-auto">
                      Stiamo attivando le donazioni online tramite SumUp: a breve potrai donare direttamente
                      da questa pagina con un semplice click, scegliendo liberamente l'importo.
                    </p>
                    <p className="text-sm text-[#5C5C5C] mb-6">
                      Nel frattempo puoi contattarci per sostenere subito l'associazione:
                    </p>
                    <div className="flex flex-col sm:flex-row justify-center gap-3">
                      <a
                        href={`mailto:${SCHOOL.email}?subject=Voglio sostenere Amici per la Musica APS`}
                        data-testid="support-email-cta"
                        className="bg-[#A3133A] text-white px-6 py-3 text-[12px] tracking-eyebrow rounded-sm inline-flex items-center justify-center gap-2 hover:bg-[#8B0F2F] transition-all"
                      >
                        <Heart size={14} /> Scrivici per donare
                      </a>
                      <Link
                        to="/contatti"
                        data-testid="support-contact-cta"
                        className="border border-[#A3133A]/40 text-[#A3133A] px-6 py-3 text-[12px] tracking-eyebrow rounded-sm inline-flex items-center justify-center gap-2 hover:bg-[#A3133A] hover:text-white transition-all"
                      >
                        Contattaci
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Come useremo il contributo */}
          <section className="bg-[#A3133A]/5 py-20 px-6 md:px-12 lg:px-24">
            <div className="max-w-[1400px] mx-auto">
              <div className="text-[11px] tracking-eyebrow text-[#A3133A] mb-5">Dove va il tuo contributo</div>
              <h2 className="font-serif-display text-3xl md:text-4xl text-[#1C1C1C] leading-tight mb-12">
                Come useremo <em className="italic font-light">la tua donazione.</em>
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#A3133A]/15 border border-[#A3133A]/15" data-testid="usi-grid">
                {(cf?.usi || []).map((uso, i) => {
                  const Icon = USE_ICONS[i % USE_ICONS.length];
                  return (
                    <div key={uso.titolo} className="bg-[#FBFBF9] p-8" data-testid={`uso-${i}`}>
                      <Icon size={26} className="text-[#A3133A] mb-5" />
                      <h3 className="font-serif-display text-xl text-[#1C1C1C] mb-3">{uso.titolo}</h3>
                      <p className="text-sm text-[#5C5C5C] leading-relaxed">{uso.testo}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          {/* Altri modi per sostenere */}
          <section className="py-20 px-6 md:px-12 lg:px-24 max-w-[1400px] mx-auto">
            <div className="text-[11px] tracking-eyebrow text-[#A3133A] mb-5">Non solo donazioni</div>
            <h2 className="font-serif-display text-3xl md:text-4xl text-[#1C1C1C] leading-tight mb-12">
              Altri modi per <em className="italic font-light">starci vicino.</em>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="border border-[#A3133A]/15 p-8" data-testid="altro-socio">
                <Users size={26} className="text-[#A3133A] mb-5" />
                <h3 className="font-serif-display text-xl text-[#1C1C1C] mb-3">Diventa socio</h3>
                <p className="text-sm text-[#5C5C5C] leading-relaxed mb-5">
                  Entra a far parte dell'associazione e partecipa attivamente alla vita della scuola.
                </p>
                <Link to="/area-soci" className="text-[12px] tracking-eyebrow text-[#A3133A] link-underline inline-flex items-center gap-2">
                  Area Soci <ExternalLink size={12} />
                </Link>
              </div>
              <div className="border border-[#A3133A]/15 p-8" data-testid="altro-eventi">
                <Music size={26} className="text-[#A3133A] mb-5" />
                <h3 className="font-serif-display text-xl text-[#1C1C1C] mb-3">Vieni ai nostri concerti</h3>
                <p className="text-sm text-[#5C5C5C] leading-relaxed mb-5">
                  La tua presenza agli eventi e ai saggi è il sostegno più bello per i nostri allievi.
                </p>
                <Link to="/eventi" className="text-[12px] tracking-eyebrow text-[#A3133A] link-underline inline-flex items-center gap-2">
                  Eventi <ExternalLink size={12} />
                </Link>
              </div>
              <div className="border border-[#A3133A]/15 p-8" data-testid="altro-condividi">
                <Share2 size={26} className="text-[#A3133A] mb-5" />
                <h3 className="font-serif-display text-xl text-[#1C1C1C] mb-3">Passa parola</h3>
                <p className="text-sm text-[#5C5C5C] leading-relaxed mb-5">
                  Condividi la campagna e i nostri contenuti sui social: anche questo è sostegno.
                </p>
                <a href={SCHOOL.social.facebook} target="_blank" rel="noreferrer" className="text-[12px] tracking-eyebrow text-[#A3133A] link-underline inline-flex items-center gap-2">
                  Seguici <ExternalLink size={12} />
                </a>
              </div>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
